import { ChangeDetectorRef, Component, Injector, Input, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AppComponentBase } from "@shared/app-component-base";
import { ConsultaDto, ConsultaServiceProxy, TipoUsuarioEnum, VideoSessionDto } from "@shared/service-proxies/service-proxies";
import Swal, { SweetAlertResult } from "sweetalert2";
import { ConsultaService } from "./../../services/consulta.service";
import { SignalRAspNetCoreHelper } from "@shared/helpers/SignalRAspNetCoreHelper";
@Component({
    selector: 'app-video-call',
    templateUrl: './video-call.component.html',
    styleUrls: ['./video-call.component.scss']
})
export class VideoCallComponent extends AppComponentBase implements OnInit, OnDestroy {
    @Input() consulta: ConsultaDto;
    private _subscriptions: Subscription[] = [];
    private remoteSubscribers: any[] = [];
    private session: any;
    private publisher: any;
    private consultaId: string;

    protected videoEnabled: boolean = true;
    protected audioEnabled: boolean = true;
    protected remoteAudioMuted: boolean = false;

    constructor(
        public injector: Injector,
        private _consultaService: ConsultaServiceProxy,
        private router: Router,
        private readonly consultaService: ConsultaService,
        private readonly cdr: ChangeDetectorRef,
        private _route: ActivatedRoute,
    ) {
        super(injector);
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this._disconnectSession();
            }
        });
    }

    ngOnInit(): void {
        this._route.params.subscribe(params => {
            this.consultaId = params['id'];
        });
        this._subscriptions.push(this._getTeleConsulta());
        SignalRAspNetCoreHelper.initSignalR();
        SignalRAspNetCoreHelper.initSignalR(() => {
            this.consultaService.initConnection();
        });

        ConsultaService.atualizarConsulta.asObservable().subscribe(() => {
            this.getConsulta();
        })
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(s => s.unsubscribe());
        this._disconnectSession();
    }

    protected get isAlone(): boolean {
        if (!this.session) {
            return true;
        }

        if (!this.session.connections) {
            return true;
        }

        return this.session.connections.length() <= 1;
    }

    protected get waitingText(): string {
        switch (this.appSession.user.tipoUsuarioEnum) {
            case TipoUsuarioEnum._0:
                return "Aguardando profissional de saúde..."
            case TipoUsuarioEnum._2:
                return "Aguardando paciente..."

            default:
                return "Aguardando";
        }
    }

    protected toggleVideo() {
        this.videoEnabled = !this.videoEnabled;
        this.publisher.publishVideo(this.videoEnabled);
    }

    protected toggleAudio() {
        this.audioEnabled = !this.audioEnabled;
        this.publisher.publishAudio(this.audioEnabled);
    }

    protected toggleRemoteAudio() {
        this.remoteAudioMuted = !this.remoteAudioMuted;

        this.remoteSubscribers.forEach(subscriber => {
            subscriber.subscribeToAudio(!this.remoteAudioMuted);
        });
    }

    protected toggleFullscreen() {
        const elem = document.querySelector('.video-card-body') as HTMLElement;

        if (!document.fullscreenElement) {
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if ((elem as any).webkitRequestFullscreen) { /* Safari */
                (elem as any).webkitRequestFullscreen();
            } else if ((elem as any).msRequestFullscreen) { /* IE11 */
                (elem as any).msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) { /* Safari */
                (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) { /* IE11 */
                (document as any).msExitFullscreen();
            }
        }
    }

    protected leaveCall() {
        Swal.fire({
            title: "Atenção",
            icon: "warning",
            html: "Deseja realmente sair da consulta?",
            showCloseButton: false,
            showCancelButton: true,
            focusConfirm: false,
            allowOutsideClick: false,
            confirmButtonText: "Sim",
            confirmButtonColor: "#E2A150",
            cancelButtonText: "Não",
            cancelButtonColor: "#6c757d"
        }).then((result: SweetAlertResult) => {
            if (result.isConfirmed) {
                this._disconnectSession();
                localStorage.setItem('consultaFinalizada', JSON.stringify({
                    id: this.consultaId,
                    data: new Date().toISOString()
                }));

                window.close();
            }
        });
    }

    private _getTeleConsulta(): Subscription {
        return this._consultaService
            .getTeleConsulta(this.consulta.id)
            .pipe(
                finalize(() => {
                    abp.ui.clearBusy();
                })
            )
            .subscribe((videoSession: VideoSessionDto) => {
                this.initializeSession(videoSession);
            });
    }

    handleError(error) {
        if (error) {
            alert(error.message);
        }
    }

    initializeSession(config: VideoSessionDto) {
        const OT = require('@vonage/client-sdk-video');

        this.session = OT.initSession(config.applicationId, config.sessionId);

        this.publisher = OT.initPublisher("publisher", {
            insertMode: "append",
            width: "100%",
            height: "100%",
            showControls: false
        }, this.handleError);

        this.session.connect(config.token, (error) => {
            if (error) {
                this.handleError(error);
            } else {
                this.session.publish(this.publisher);
            }
        });

        this.session.on("streamCreated", (event) => {

            if (event.stream.connection.connectionId === this.session.connection.connectionId) {
                return;
            }

            const subscriber = this.session.subscribe(event.stream, "subscriber", {
                insertMode: "append",
                width: "100%",
                height: "100%",
                showControls: false,
                subscribeToAudio: !this.remoteAudioMuted
            }, this.handleError);

            this.remoteSubscribers.push(subscriber);
        });

    }

    private _disconnectSession() {
        if (this.session) {
            this.session.disconnect();
            this.session = null;
        }

        if (this.publisher) {
            this.publisher.destroy();
            this.publisher = null;
        }
    }

    private getConsulta() {
        this._consultaService
            .get(this.consulta.id)
            .subscribe((result: ConsultaDto) => {
                this.consulta = result;
                this.cdr.detectChanges();
            });
    }
}
